import { createApiClient } from './zodios/ecommerce';
import { createApiClient as createMediaApiClient } from './zodios/media';
import { createApiClient as createAdminApiClient } from './zodios/admin';
import { eraseCookie, getCookie, setCookie } from '../cookie';
import { pluginToken } from './plugins/pluginToken';
import { getEnv } from '../env';
import {
  setAuthSession,
  setMediaToken,
  COOKIE_TOKEN_MEDIA,
  COOKIE_TOKEN_API,
  COOKIE_EXPIRY,
} from '../repo/session';
import { resetRepositories } from '../repo';
export { schemas } from './zodios/ecommerce';
export { schemas as adminSchemas } from './zodios/admin';

function newApiClient() {
  if (typeof window !== 'undefined') {
    return createApiClient('/');
  }
  const env = getEnv();
  const nodeEnv = env.NODE_ENV;
  let baseUrl = env.API_PATH;
  if (!baseUrl) {
    baseUrl =
      nodeEnv == 'development'
        ? 'http://localhost:8089'
        : 'https://next.reportdev-datalakestock.net';
  }
  return createApiClient(baseUrl);
}

function newMediaApiClient() {
  const env = getEnv();
  const nodeEnv = env.NODE_ENV;
  let baseUrl = env.MEDIA_API_PATH;
  if (!baseUrl) {
    baseUrl =
      nodeEnv == 'development'
        ? 'http://localhost:8090'
        : 'https://media.reportdev-datalakestock.net';
  }
  return createMediaApiClient(baseUrl);
}

function newAdminApiClient() {
  const env = getEnv();
  const nodeEnv = env.NODE_ENV;
  let baseUrl = env.ADMIN_API_PATH;
  if (!baseUrl) {
    baseUrl =
      nodeEnv == 'development'
        ? 'http://localhost:8089'
        : 'https://admin.reportdev-datalakestock.net';
  }
  return createAdminApiClient(baseUrl);
}

const protocol = window.location.protocol;
const isSecureProtocol = protocol === 'https:';

const api = newApiClient();
const mediaApi = newMediaApiClient();
const adminApi = newAdminApiClient();

let uploadedMediaAuth = 'ecommerce';

export function useAdminMediaAuth() {
  uploadedMediaAuth = 'admin';
}

const after: ReturnType<typeof pluginToken> = {
  name: 'after',

  request: async (api, config) => {
    const conf = { ...config, withCredentials: true };
    return conf;
  },

  response: async (api, config, response) => {
    try {
      const {
        data: {
          item: { jwt, user, roles },
        },
      } = response.data;
      setCookie(
        COOKIE_TOKEN_API,
        jwt,
        new Date(new Date().getTime() + COOKIE_EXPIRY),
        !isSecureProtocol
      );
      setAuthSession(jwt, user, roles);
    } catch (e) {
      console.warn(e);
    }
    return response;
  },
};

const afterLogout: ReturnType<typeof pluginToken> = {
  name: 'afterLogout',

  response: async (api, config, response) => {
    try {
      eraseCookie(COOKIE_TOKEN_API, !isSecureProtocol);
      eraseCookie(COOKIE_TOKEN_MEDIA, !isSecureProtocol);
      setAuthSession('', undefined, []);
      resetRepositories();
    } catch (e) {
      console.warn(e);
    }
    return response;
  },
};

const mediaAfter: ReturnType<typeof pluginToken> = {
  name: 'after',

  request: async (api, config) => config,

  response: async (api, config, response) => {
    try {
      const jwt = response.data.data.item.jwt;
      setCookie(
        COOKIE_TOKEN_MEDIA,
        jwt,
        new Date(new Date().getTime() + COOKIE_EXPIRY),
        !isSecureProtocol
      );
      setMediaToken(jwt);
    } catch (e) {
      console.warn(e);
    }
    return response;
  },
};

if (typeof window !== 'undefined') {
  api.use('post', '/api/onboarding/create-account', after);
  api.use('post', '/api/auth/signin', after);
  api.use('post', '/api/auth/token-signin', after);
  api.use('get', '/api/auth/refresh', after);
  api.use('post', '/api/auth/signout', afterLogout);
  api.use('post', '/api/auth/one-time-login', after);
  api.use(
    pluginToken({
      getToken: async () => getCookie(COOKIE_TOKEN_API) ?? undefined,
      renewToken: async () => {
        const {
          data: {
            item: { jwt },
          },
        } = await api.get('/api/auth/refresh');
        return jwt;
      },
    })
  );
  adminApi.use(
    pluginToken({
      getToken: async () => getCookie(COOKIE_TOKEN_API) ?? undefined,
      renewToken: async () => {
        const {
          data: {
            item: { jwt },
          },
        } = await api.get('/api/auth/refresh');
        return jwt;
      },
    })
  );

  api.use('post', '/api/auth-media', mediaAfter);
  adminApi.use('post', '/api/auth-media', mediaAfter);
  mediaApi.use(
    pluginToken({
      getToken: async () => getCookie(COOKIE_TOKEN_MEDIA) ?? undefined,
      renewToken: async () => {
        const auth =
          uploadedMediaAuth === 'admin'
            ? adminApi.mediaAuth
            : api.uploadedMediaAuth;
        const {
          data: {
            item: { jwt },
          },
        } = await auth({});
        return jwt;
      },
    })
  );
}

function initSession() {
  api.authRefresh().catch((e) => {
    console.warn(e?.message);
  });
  setInterval(() => {
    api.authRefresh().catch((e) => {
      console.warn(e?.message);
    });
  }, COOKIE_EXPIRY - 5000);
}

initSession();

export { api, mediaApi, adminApi };
